import { useStatusOverlay } from '@fs/zion-ui'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from '@fs/zion-router'
import { useCacheService } from '@fs/zion-cache-service'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { type GroupDetails, type GroupId } from '@fs/zion-tree-types'
import { isAxiosError } from 'axios'
import { cacheService } from './caches'
import { useInterval } from '../helpers/utils'
/**
 * UseGroup.
 * @param {GroupId} groupId - groupId
 * @returns {{data, loading, error, refetch}} the return object
 * @throws 400 - The group id was invalid.
 * @throws 403 - Only un-banished group members can view a group.
 * @throws 404 - Group not found.
 * @throws 410 - Group is tombstoned.
 */
export default function useGroup<T extends GroupDetails>(
  groupId: GroupId | undefined
): Omit<ReturnType<typeof useCacheService<T, 'cache-and-network'>>[0], 'loading'> & {
  loading: boolean
  refetch: ReturnType<typeof useCacheService<T, 'cache-and-network'>>[1]
} {
  const [t] = useTranslation()
  const { subPage } = useParams()
  const refetchAttemptsRef = useRef(0)
  const showStatusOverlay = useStatusOverlay()
  const url = groupId ? `/service/tree/tree-data/groups/${groupId}` : undefined
  const history = useHistory()
  const [requestState, refetch] = useCacheService<T, 'cache-and-network'>(cacheService, url, url ?? '')
  const { canAccessFGT } = useGroupsContext()
  const { error } = requestState
  const reloadingImage = useRef(false)

  // Polling for image being processed/uploaded/imported
  // This useEffect will refetch the group up to five times. After five times it gives up.
  const interval = requestState.data?.imagePending && refetchAttemptsRef.current < 5 ? 1000 : null
  useInterval(() => {
    refetchAttemptsRef.current += 1
    reloadingImage.current = true
    // eslint-disable-next-line promise/catch-or-return -- the error is handled in the useEffect below
    refetch().finally(() => {
      reloadingImage.current = false
    })
  }, interval)

  useEffect(() => {
    if (isAxiosError(error)) {
      console.error(error)
      const errorStatus = error.response?.status
      // Group has been deleted or bad group id
      if (errorStatus && errorStatus >= 400 && errorStatus < 500) {
        history.replace(`/${subPage}`)
      } else {
        showStatusOverlay({ type: 'error', message: t('group-management.group-data.error') })
      }
    }
  }, [showStatusOverlay, error, t, history, subPage])

  // If user is blocked from FGT, we want the group to hide all private person sharing,
  // so we make the group look like a non-private person sharing group
  if (!canAccessFGT && requestState?.data?.types) {
    requestState.data.types = requestState.data.types.filter((type) => type !== 'PRIVATE_PERSON_SHARING')
  }
  return { ...requestState, loading: (requestState.loading || !requestState.data) && !reloadingImage.current, refetch }
}
